import React, {
  CSSProperties,
  ElementType,
  FC,
  memo,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react'
import { INDIVIDUAL_SEPARATOR_IDENTIFIER } from '../../constants'
import useItemContext from '../../hooks/use-item-context'
import CollapseHierarchy from '../CollapseHierarchy'

export interface ItemRendererProps {
  id: string
  node: any
  style: CSSProperties
  renderer: ElementType
  observe: boolean
}

const ItemRenderer: FC<ItemRendererProps> = memo(
  ({ node, renderer: Renderer, id, style, observe }) => {
    const ref = useRef<HTMLDivElement>()
    const {
      store: {
        resizeObserver,
        rootId,
        store: { hierarchyCollapseStore }
      }
    } = useItemContext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialHeight = useMemo(() => node.height, [id, node?.height])
    useLayoutEffect(() => {
      const ele = ref.current
      if (ref.current && ref.current?.lastChild && observe) {
        resizeObserver.observe(ref.current.lastChild)
      }
      return () => observe && ele?.lastChild && resizeObserver.unobserve(ele.lastChild)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, observe])
    const showCollapse =
      Boolean(hierarchyCollapseStore) &&
      id !== rootId &&
      !id.includes(INDIVIDUAL_SEPARATOR_IDENTIFIER) &&
      hierarchyCollapseStore.isVisible(id)
    return (
      <div
        ref={ref}
        data-id={id}
        style={{
          ...style,
          minHeight: initialHeight,
          maxHeight: node.height,
          height: node.height
        }}
      >
        {showCollapse ? (
          <div
            style={{
              height: 'fit-content'
            }}
          >
            <CollapseHierarchy id={id} />
            <Renderer key={id} id={id} />
          </div>
        ) : (
          <Renderer key={id} id={id} />
        )}
      </div>
    )
  }
)

export default ItemRenderer
